import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import dayjs from 'dayjs';
import { t202Api, FT120, CreateT202Request, UpdateT202Request } from '../../../services/t202Api';
import { T202Mode } from './useT202Mode';

export interface T202FormData {
  ft120_11: string;   // 公告開始日
  ft120_12: string;   // 公告結束日
  ft120_13: string;   // 申請人員工代碼
  ft120_14: string;   // 申請部門代碼
  ft120_15: string;   // 申請日期
  ft120_16: number;   // 分類碼
  ft120_20: string;   // 附加欄位
  ft120_21: string;   // 公告主題
  ft120_22: string;   // 公告內容
}

const emptyForm = (): T202FormData => ({
  ft120_11: '',
  ft120_12: '',
  ft120_13: '',
  ft120_14: '',
  ft120_15: dayjs().format('YYYY-MM-DD'),
  ft120_16: 0,
  ft120_20: '',
  ft120_21: '',
  ft120_22: '',
});

function recordToForm(r: FT120): T202FormData {
  return {
    ft120_11: r.ft120_11 || '',
    ft120_12: r.ft120_12 || '',
    ft120_13: r.ft120_13 || '',
    ft120_14: r.ft120_14 || '',
    ft120_15: r.ft120_15 || '',
    ft120_16: r.ft120_16 || 0,
    ft120_20: r.ft120_20 || '',
    ft120_21: r.ft120_21 || '',
    ft120_22: r.ft120_22 || '',
  };
}

export function useT202Record(mode: T202Mode, enterBrowse: () => void) {
  const [records, setRecords] = useState<FT120[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [formData, setFormData] = useState<T202FormData>(emptyForm());
  const [loading, setLoading] = useState(false);

  const current: FT120 | null =
    currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;

  // Load all records
  const loadRecords = useCallback(async (): Promise<FT120[]> => {
    setLoading(true);
    try {
      const res = await t202Api.list(1, 500);
      const data = res.data || [];
      setRecords(data);
      return data;
    } catch {
      message.error('載入資料失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form when current record or mode changes
  useEffect(() => {
    if (current && mode === 'browse') {
      setFormData(recordToForm(current));
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = useCallback(() => {
    if (records.length > 0) setCurrentIdx(0);
  }, [records.length]);

  const navPrev = useCallback(() => {
    if (currentIdx > 0) setCurrentIdx(prev => prev - 1);
  }, [currentIdx]);

  const navNext = useCallback(() => {
    if (currentIdx < records.length - 1) setCurrentIdx(prev => prev + 1);
  }, [currentIdx, records.length]);

  const navLast = useCallback(() => {
    if (records.length > 0) setCurrentIdx(records.length - 1);
  }, [records.length]);

  // Start add
  const startAdd = useCallback(() => {
    setFormData(emptyForm());
  }, []);

  // Start edit
  const startEdit = useCallback(() => {
    if (current) {
      setFormData(recordToForm(current));
    }
  }, [current]);

  // Cancel / restore
  const cancel = useCallback(() => {
    if (current) {
      setFormData(recordToForm(current));
    } else {
      setFormData(emptyForm());
    }
    enterBrowse();
  }, [current, enterBrowse]);

  // Save
  const save = useCallback(async () => {
    setLoading(true);
    try {
      let savedId = '';
      if (mode === 'add') {
        const req: CreateT202Request = { ...formData };
        const res = await t202Api.create(req);
        savedId = res.ft120_01;
        message.success('新增成功');
      } else if (mode === 'edit' && current) {
        const req: UpdateT202Request = { ...formData };
        await t202Api.update(current.ft120_01, req);
        savedId = current.ft120_01;
        message.success('修改成功');
      }
      const data = await loadRecords();
      const idx = data.findIndex(r => r.ft120_01 === savedId);
      setCurrentIdx(idx >= 0 ? idx : 0);
      enterBrowse();
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [mode, formData, current, loadRecords, enterBrowse]);

  // Delete
  const doDelete = useCallback(async () => {
    if (!current) return;
    setLoading(true);
    try {
      await t202Api.delete(current.ft120_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
      enterBrowse();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [current, currentIdx, loadRecords, enterBrowse]);

  // Quick search
  const quickSearch = useCallback(async (keyword: string) => {
    setLoading(true);
    try {
      const res = await t202Api.list(1, 500, keyword);
      const data = res.data || [];
      setRecords(data);
      if (data.length > 0) {
        setCurrentIdx(0);
      } else {
        setCurrentIdx(-1);
        message.info('查無資料');
      }
    } catch {
      message.error('查詢失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const setField = useCallback(<K extends keyof T202FormData>(
    field: K,
    value: T202FormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  return {
    records,
    current,
    currentIdx,
    formData,
    loading,
    setField,
    navFirst,
    navPrev,
    navNext,
    navLast,
    startAdd,
    startEdit,
    cancel,
    save,
    doDelete,
    quickSearch,
    loadRecords,
  };
}
